import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/core/auth/auth.context'
import { formatError } from '@/core/utils/error.formatter'
import { ThemeToggle } from '@/shared/components/theme'
import { Icon } from '@/shared/components/icons'

export function LoginPage() {
  const { signIn } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(false)
  const [reveal, setReveal] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const firstField = useRef<HTMLInputElement>(null)
  useEffect(() => {
    firstField.current?.focus()
  }, [])

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!username.trim() || !password) return

    setBusy(true)
    setError(null)
    try {
      await signIn(username.trim(), password, remember)
      // No navigation needed: the guard re-renders the app once a user exists.
    } catch (err) {
      setError(formatError(err).message)
      // Keep the username so a typo in the password costs one field, not two.
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-shell">
      <div className="login-corner">
        <ThemeToggle />
      </div>

      <form className="login-card" onSubmit={submit} noValidate>
        <div className="brand" style={{ padding: 0, marginBottom: 22 }}>
          <span className="brand-mark" aria-hidden="true">
            <Icon.graph />
          </span>
          Context Graph
        </div>

        <h1 style={{ fontSize: 25 }}>Sign in</h1>
        <p className="muted" style={{ margin: '7px 0 22px' }}>
          Enter your credentials to reach the workspace.
        </p>

        <div className="stack" style={{ gap: 15 }}>
          <div className="field">
            <label className="field-label" htmlFor="login-username">
              Username
            </label>
            <input
              id="login-username"
              ref={firstField}
              className="input"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="Your username"
              value={username}
              disabled={busy}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="login-password">
              Password
            </label>
            <div className="row-tight" style={{ flexWrap: 'nowrap' }}>
              <input
                id="login-password"
                className="input"
                type={reveal ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Your password"
                value={password}
                disabled={busy}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setReveal((v) => !v)}
                aria-label={reveal ? 'Hide password' : 'Show password'}
              >
                {reveal ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <label className="switch-row" htmlFor="login-remember">
            <input
              id="login-remember"
              type="checkbox"
              className="switch"
              checked={remember}
              disabled={busy}
              onChange={(e) => setRemember(e.target.checked)}
            />
            <span>Keep me signed in</span>
          </label>

          {error && (
            <div className="callout callout-danger" role="alert">
              <p className="small">{error}</p>
            </div>
          )}

          <button
            className="btn btn-primary btn-block"
            type="submit"
            disabled={busy || !username.trim() || !password}
            style={{ padding: '10px 16px', fontSize: 14.5 }}
          >
            {busy && <span className="spinner" />}
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </div>

        <p className="small faint" style={{ marginTop: 18, marginBottom: 0 }}>
          Accounts are provisioned by an administrator — there is no self-service
          sign-up.
        </p>
      </form>
    </div>
  )
}
